import { CaptchaInput } from "@/components/Captcha";
import HBIcon from "@/components/HBIcon";
import HBInfoDialog from "@/components/HBModal/info";
import { TelInput } from "@/components/TelInput";
import VerifyConfirm from "@/components/Verify/Confirm";
import useAppStore from "@/context/store";
import { isGdMerged } from "@/utils/gaoding";
import { useLoginAutoMerge } from "@/utils/gdMerge";
import { HBErrCode } from "@/utils/requestHandler/customErrCode";
import { useMemoizedFn, useThrottleFn } from "ahooks";
import { Button, Form, FormInstance } from "antd";
import classNames from "classnames";
import { HTMLAttributes, useCallback, useMemo, useRef, useState } from "react";
import { checkNeedRestPassword, getMe, login, logout } from "../../../services";
import { isEnter } from "../../../utils/keyBoard";
import HBInput from "../../HBInput";
import HBMessage from "../../HBMessage";
import styles from "../index.module.less";
import { isDestoryingAccount, openDestoryAccountInfo } from "../loginErrHandle";
import accountLocked from "../shared/accountLocked";
import Password from "../shared/password";
import rules from "../shared/rules";
import { ThirdLogin } from "../shared/third";
import UserAgreement from "../shared/userAgreement";

interface TelInputValue {
  value: string;
  areaCode: string;
  number: string;
}

interface EmailFormValue {
  email: string;
  password: string;
}

interface TelFormValue {
  tel: TelInputValue;
  password: string;
}

type LoginFormValue = EmailFormValue | TelFormValue;

// 代理直充的用户需要判断是否是重置了密码，判断用户是否同意了协议
const EMAIL_STATUS = {
  NORMAL: "nomarl",
  RESET: "reset",
  CONFIRM: "confirm",
};

export enum LoginType {
  PWD,
  CAPTCHA,
  EMAIL,
  WECHAT = "wechat",
  /**重置密码 */
  RESET = "reset",
  /**注册 */
  REGISTER = "register",
}

type TelValue =
  | string
  | {
      value: string;
      areaCode: string;
      number: string;
    };

interface ILoginProps {
  type: LoginType;
  setType: (type: LoginType) => void;
  onOk: (user: IUser) => void;
  initialTel?: TelValue;
  setDefaultTel: (value: TelValue) => void;
  onThirdLogin: (user: IUser) => void;
}

export default function Login({
  onOk,
  type,
  setType,
  initialTel = "",
  setDefaultTel,
  onThirdLogin,
  ...rest
}: ILoginProps & HTMLAttributes<HTMLDivElement>) {
  const loginFormRef = useRef<FormInstance<LoginFormValue>>(null);

  const [loginLoading, setLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState(EMAIL_STATUS.NORMAL);

  const onGoReset = useMemoizedFn(async () => {
    const email = await loginFormRef.current?.getFieldValue("email");
    const tel = await loginFormRef.current?.getFieldValue("tel");
    // 兼容手机号与邮箱
    setDefaultTel(type === LoginType.EMAIL ? email : tel);
    setType(LoginType.RESET);
  });

  const operation = useMemo(() => {
    if (type === LoginType.EMAIL) {
      return (
        <a
          data-gd-click="button_click"
          data-button-name="重置密码"
          onClick={onGoReset}
          style={{ marginLeft: "auto" }}
        >
          重置密码
        </a>
      );
    }

    if (type === LoginType.PWD) {
      return (
        <>
          <a
            data-gd-click="button_click"
            data-button-name="验证码登录"
            onClick={() => {
              setType(LoginType.CAPTCHA);
            }}
          >
            验证码登录
          </a>
          <a
            data-gd-click="button_click"
            data-button-name="重置密码"
            onClick={onGoReset}
          >
            重置密码
          </a>
        </>
      );
    }

    return (
      <a
        data-gd-click="button_click"
        data-button-name="密码登录"
        onClick={() => {
          setType(LoginType.PWD);
        }}
      >
        密码登录
      </a>
    );
  }, [type, onGoReset, setType]);

  const switchWechat = useMemoizedFn(() => {
    setType(LoginType.WECHAT);
  });

  const switchEmail = useMemoizedFn(() => {
    setType(LoginType.EMAIL);
    loginFormRef.current?.resetFields();
  });

  const autoMerge = useLoginAutoMerge(true);

  const { run: onLogin } = useThrottleFn(
    async () => {
      if (emailStatus === EMAIL_STATUS.RESET) {
        return;
      } else if (emailStatus === EMAIL_STATUS.CONFIRM) {
        const agree = await new Promise((resolve) => {
          HBInfoDialog.open({
            title: "温馨提示",
            content: (
              <div className={styles.protocol}>
                注册登录即代表同意
                <a target="_blank" href="/about/disclaimer">
                  《花瓣用户协议》
                </a>
                <a target="_blank" href="/about/disclaimer?activeKey=1">
                  《花瓣隐私政策》
                </a>
              </div>
            ),
            cancelText: "取消",
            okText: "已阅读并同意",
            onOk: () => {
              resolve(true);
            },
            onCancel: () => {
              resolve(false);
            },
          });
        });
        if (!agree) {
          return;
        }
      }
      if (!loginFormRef.current) {
        return;
      }
      setLoading(true);
      try {
        const values = await loginFormRef.current.validateFields();
        let result, user;
        try {
          result = await login({
            ...values,
            ...(emailStatus === EMAIL_STATUS.CONFIRM
              ? { agree_protocol: true }
              : null),
            email: (type === LoginType.EMAIL
              ? (values as EmailFormValue).email
              : (values as TelFormValue).tel?.value
            ).trim(),
          });

          if (!result.err) {
            user = await getMe();
            if (!isGdMerged(user) && user?.tel) {
              await autoMerge(user);
              // 获取一下最新的user信息
              user = useAppStore.getState().user || user;
            }
          }

          setLoading(false);

          if (result.err === HBErrCode.verify) {
            //触发滑块验证
            VerifyConfirm({
              captchakey: result.captchakey,
              title: result.msg,
              onOk() {
                // 滑块验证通过后，自动登录
                onLogin();
              },
            });
          }

          // 账号冻结处理
          if (result.err === 403) {
            if (result.msg === "您已在其他窗口登录，请勿重复登录") {
              HBInfoDialog.open({
                title: "提醒",
                content: result.msg,
                cancelText: "切换账号",
                okText: "确认",
                onOk: async () => {
                  const user = await getMe();

                  if (!user.err) {
                    onOk(user);
                  } else {
                    HBMessage.error(user.msg || "获取用户信息失败");
                  }
                },
                async onCancel() {
                  await logout();
                  HBMessage.info("已退出登录，请重新登录");
                },
              });

              return;
            } else if (isDestoryingAccount(result)) {
              openDestoryAccountInfo();
            } else {
              accountLocked(result);
              return;
            }
          }

          if (result.err) {
            HBMessage.error(result.msg || "登录失败");
            return;
          }
        } catch (error) {
          // donothing
        }

        if (!user.err) {
          onOk(user);
        } else {
          HBMessage.error(user.msg || "获取用户信息失败");
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
      }
    },
    { wait: 300 }
  );

  /**
   * 检查用户账号
   * 渠道用户直充注册账号检查，需要让用户同意协议
   */
  const onChangePreCheck = useCallback(async (tel) => {
    let value = tel.value;

    // 简单校验手机号格式
    const isChina = tel.areaCode === "+86";
    // 稿定参考 https://git.gaoding.com/gaoding/auth/blob/6042c6efaccb26a5139d6c6927c91b0bb42cb0c4/packages/auth-next/src/utils/validator.ts
    const telCheck = isChina
      ? /^1[3-9]\d{9}$/.test(tel.number)
      : /^\d{6,}$/.test(tel.number);

    if (!telCheck) {
      setEmailStatus(EMAIL_STATUS.NORMAL);
      return;
    }

    const result = await checkNeedRestPassword({ tel: value });

    if (result?.server_force_reset) {
      setEmailStatus(EMAIL_STATUS.RESET);
    } else if (result?.auto_reg && !result?.agree_protocol) {
      setEmailStatus(EMAIL_STATUS.CONFIRM);
    } else {
      setEmailStatus(EMAIL_STATUS.NORMAL);
    }
  }, []);

  return (
    <div
      {...rest}
      className={styles.login}
      data-dialog-title={
        type === LoginType.CAPTCHA ? "手机验证码登录弹窗" : "登录弹窗"
      }
      data-gd-expose="dialog_expose"
      data-unique-id={type}
    >
      <Form className={styles.form} ref={loginFormRef}>
        {LoginType.EMAIL === type ? (
          <Form.Item
            className={styles.item}
            name="email"
            help={
              emailStatus === EMAIL_STATUS.RESET ? (
                <span className="ant-form-item-explain-error">
                  为保障您的账号安全，请重置密码
                </span>
              ) : null
            }
            rules={rules.email}
            initialValue=""
            validateTrigger="onSubmit"
          >
            <HBInput
              placeholder="邮箱"
              onChange={() => {
                setEmailStatus(EMAIL_STATUS.NORMAL);
              }}
              autofillCheck
              autoFocus
            />
          </Form.Item>
        ) : (
          <Form.Item
            className={styles.item}
            name="tel"
            help={
              emailStatus === EMAIL_STATUS.RESET ? (
                <span className="ant-form-item-explain-error">
                  为保障您的账号安全，请重置密码
                </span>
              ) : null
            }
            rules={rules.tel}
            initialValue={
              typeof initialTel === "string"
                ? { areaCode: "+86", value: initialTel, number: initialTel }
                : initialTel
            }
            validateTrigger="onSubmit"
          >
            <TelInput
              placeholder="手机号"
              onChange={onChangePreCheck}
              autofillCheck
              autoFocus
            />
          </Form.Item>
        )}
        {type === LoginType.CAPTCHA ? (
          <Form.Item
            className={classNames([styles.item, styles.short])}
            name="login_code"
            rules={rules.captcha}
          >
            <CaptchaInput
              tel={() => {
                // 获取手机号/邮箱
                const tel = loginFormRef.current?.getFieldValue("tel");
                return tel?.value || tel;
              }}
            />
          </Form.Item>
        ) : (
          <Form.Item
            className={classNames([styles.item, styles.short])}
            name="password"
            rules={rules.password}
          >
            <Password
              placeholder="密码"
              autofillCheck
              onKeyDown={(e: KeyboardEvent) => {
                if (isEnter(e)) {
                  onLogin();
                }
              }}
            />
          </Form.Item>
        )}
      </Form>
      <div className={styles.operation}>{operation}</div>
      <Button
        className={styles.button}
        type="primary"
        onClick={onLogin}
        loading={loginLoading}
      >
        登录
      </Button>
      <div className={styles.other}>
        {type === LoginType.EMAIL && (
          <div className={styles.phone} onClick={() => setType(LoginType.PWD)}>
            <HBIcon type="icon" />
            账号登录注册
          </div>
        )}
        <div
          className={classNames([styles.thirdLoginItem, styles.wechat])}
          data-gd-click="button_click"
          data-button-name="微信登录注册"
          onClick={switchWechat}
          style={{
            marginRight: 12,
          }}
        >
          <HBIcon className={styles.icon} type="ic_wechat1" title="微信" />
        </div>
        <ThirdLogin onOk={onThirdLogin} />
        {type !== LoginType.EMAIL && (
          <div
            className={classNames([styles.thirdLoginItem, styles.email])}
            data-gd-click="button_click"
            data-button-name="邮箱登录"
            onClick={switchEmail}
            style={{
              marginLeft: 12,
            }}
          >
            <HBIcon className={styles.icon} type="email" title="邮箱" />
          </div>
        )}
      </div>

      <UserAgreement />
    </div>
  );
}
